import { isLibraryDoc } from "./folder-filters.js";
import type { DocCategory, DocSummary } from "./types.js";

/**
 * The folder TREE of the Documentation section, read out of the ONE flat
 * list `GET /api/documentation/categories` returns (2026-10-31).
 *
 * portal-api now builds that list from what THIS company may read
 * (`Documents::FolderList` over the AccessResolver): only folders staff
 * allowed for it, plus the folders above them so an allowed sub-folder keeps
 * its place, each with `parent_id`, its own readable `document_count` and a
 * `total_count` that adds its sub-folders'. It used to be every folder ticked
 * for resellers, flat — and this page rendered every row as a top-level card,
 * so "Videos / R11.0 / Command Control" read as three unrelated topics.
 *
 * These helpers are the one place the tree is put back together, so the
 * landing grid, the folder page and its breadcrumb agree. The ORDER is the
 * command center's (`doc-sidebar/tree.ts` → `buildTree`): top-level folders
 * by what they hold, busiest first, then name; sub-folders by name at every
 * level. A folder whose parent is not in the list is shown at the top rather
 * than lost.
 */

/** Is `parent_id` a folder this list carries? */
function parentIn(category: DocCategory, all: readonly DocCategory[]): DocCategory | undefined {
  if (category.parent_id == null || category.parent_id === category.id) return undefined;
  return all.find((c) => c.id === category.parent_id);
}

/** A folder's direct sub-folders in the list, by name. */
export function subFolders(category: DocCategory, all: readonly DocCategory[] | undefined): DocCategory[] {
  return (all ?? [])
    .filter((c) => c.id !== category.id && c.parent_id === category.id)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Every folder above this one, outermost first — the breadcrumb of
 * "Videos › R11.0 › Command Control". Stops at a parent missing from the
 * list, and at a cycle. */
export function ancestorFolders(category: DocCategory, all: readonly DocCategory[] | undefined): DocCategory[] {
  const list = all ?? [];
  const chain: DocCategory[] = [];
  let parent = parentIn(category, list);
  while (parent && parent.id !== category.id && !chain.includes(parent)) {
    chain.unshift(parent);
    parent = parentIn(parent, list);
  }
  return chain;
}

/**
 * How many articles a folder holds, sub-folders included. portal-api's
 * `total_count` when it sends one (it is counted from what this company can
 * read). An older portal-api sends none, and then the library documents
 * filed in the folder or below it are counted here.
 */
export function folderCount(
  category: DocCategory,
  all: readonly DocCategory[] | undefined,
  docs: readonly DocSummary[] = [],
): number {
  if (typeof category.total_count === "number") return category.total_count;
  const ids = new Set<number>();
  const walk = (c: DocCategory): void => {
    if (ids.has(c.id)) return;
    ids.add(c.id);
    for (const child of subFolders(c, all)) walk(child);
  };
  walk(category);
  return docs.filter((d) => isLibraryDoc(d) && d.category != null && ids.has(d.category.id)).length;
}

/** The top-level folders, busiest first, then by name. */
export function topLevelFolders(
  all: readonly DocCategory[] | undefined,
  docs: readonly DocSummary[] = [],
): DocCategory[] {
  const list = all ?? [];
  return list
    .filter((c) => parentIn(c, list) === undefined)
    .map((c) => ({ c, n: folderCount(c, list, docs) }))
    .sort((a, b) => (a.n !== b.n ? b.n - a.n : a.c.name.localeCompare(b.c.name)))
    .map((x) => x.c);
}
